import React, { ReactNode } from 'react';
import { Variant } from '@alltrails/shared/components/FormSelectorBase';
import Button from 'components/shared/Button';
import ExpandableFilterPanel from 'components/explore/ExpandableFilterPanel';
import FilterInputFactory from 'components/explore/filters/FilterInputFactory';
import Modal from 'components/shared/Modal';
import { saveFilterChanges } from 'hooks/useSearchFilters';
import { Context } from 'types/Context';
import {
  Filter,
  FilterConfig,
  FilterConfigSlider,
  FilterConfigStarRating,
  FilterConfigToggle,
  FilterInput,
  FilterRangeUpdates,
  Filters
} from 'types/Search';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import * as styles from './styles/styles.module.scss';

export type Props = {
  clearAllFilters: () => void;
  context: Context;
  enabledFilters: string[];
  filterConfigs: Filters;
  handleClose: () => void;
  handleFilterRadioToggle?: (filterKey: string, filterConfigKey: string) => void;
  handleFilterSet?: (filterKey: string, value: string) => void;
  handleFilterSlider?: (filterKey: string, value: FilterRangeUpdates) => void;
  handleFilterToggle?: (filterKey: string, filterConfigKey: string) => void;
  resultsCount: number;
};

const getMappedFilters = (filters: Filters, enabledFilters: string[]): Filter[] => {
  const pageStrings = StringConstantsMixin.strings.FILTERS;

  const allFilters: Filter[] = [
    {
      display: pageStrings.SORT_LABEL,
      key: 'sort',
      enabledKey: 'sort',
      type: FilterInput.RADIO,
      config: filters.sort as FilterConfigToggle,
      extraProps: {
        variant: Variant.INLINE_SPACE_BETWEEN,
        className: styles.fullWidth
      }
    },
    {
      display: pageStrings.DIFFICULTY,
      key: 'difficulty',
      enabledKey: 'difficulty',
      type: FilterInput.CHECKBOX,
      config: filters.difficulty as FilterConfigToggle,
      extraProps: {
        variant: Variant.INLINE_SPACE_BETWEEN,
        className: styles.fullWidth
      }
    },
    {
      display: pageStrings.LENGTH,
      key: 'lengths',
      enabledKey: 'length',
      type: FilterInput.SLIDER,
      config: filters.lengths as FilterConfigSlider
    },
    {
      display: pageStrings.ELEV_GAIN,
      key: 'elevation_gain',
      enabledKey: 'elevation_gain',
      type: FilterInput.SLIDER,
      config: filters.elevation_gain as FilterConfigSlider
    },
    {
      // No explicit label when displayed in this component. The input has its own label built in.
      display: null,
      key: 'minRating',
      enabledKey: 'rating',
      type: FilterInput.STAR_RATING,
      config: filters.minRating as FilterConfigStarRating
    },
    {
      display: pageStrings.ACTIVITY_LABEL,
      key: 'activities',
      enabledKey: 'activity',
      type: FilterInput.RADIO,
      truncate: true,
      config: filters.activities as FilterConfigToggle,
      extraProps: {
        variant: Variant.INLINE_SPACE_BETWEEN,
        className: styles.fullWidth
      }
    },
    {
      display: pageStrings.FEATURES_LABEL,
      key: 'features',
      enabledKey: 'feature',
      type: FilterInput.CHECKBOX,
      truncate: true,
      config: filters.features as FilterConfigToggle,
      extraProps: {
        variant: Variant.INLINE_SPACE_BETWEEN,
        className: styles.fullWidth
      }
    },
    {
      display: pageStrings.SUITABILITY,
      key: 'access',
      enabledKey: 'access',
      type: FilterInput.CHECKBOX,
      config: filters.access as FilterConfigToggle,
      extraProps: {
        variant: Variant.INLINE_SPACE_BETWEEN,
        className: styles.fullWidth
      }
    },
    {
      display: pageStrings.ROUTE_TYPE,
      key: 'route_type',
      enabledKey: 'route_type',
      type: FilterInput.CHECKBOX,
      config: filters.route_type as FilterConfigToggle,
      extraProps: {
        variant: Variant.INLINE_SPACE_BETWEEN,
        className: styles.fullWidth
      }
    },
    {
      display: pageStrings.TRAIL_TRAFFIC,
      key: 'visitor_usage',
      enabledKey: 'visitor_usage',
      type: FilterInput.CHECKBOX,
      config: filters.visitor_usage as FilterConfigToggle,
      extraProps: {
        variant: Variant.INLINE_SPACE_BETWEEN,
        className: styles.fullWidth
      }
    },
    {
      display: pageStrings.TRAIL_COMPLETION,
      key: 'completed',
      enabledKey: 'completed',
      type: FilterInput.CHECKBOX,
      config: filters.completed as FilterConfigToggle,
      extraProps: {
        variant: Variant.INLINE_SPACE_BETWEEN,
        className: styles.fullWidth
      }
    },
    {
      display: 'Linked',
      key: 'linked',
      enabledKey: 'linked',
      type: FilterInput.CHECKBOX,
      admin: true,
      config: filters.linked as FilterConfigToggle
    },
    {
      display: 'Parks',
      key: 'areas',
      enabledKey: 'area',
      type: FilterInput.CHECKBOX,
      admin: true,
      config: filters.areas as FilterConfigToggle,
      extraProps: {
        variant: Variant.INLINE_SPACE_BETWEEN,
        className: styles.fullWidth
      }
    },
    {
      display: 'City',
      key: 'cities',
      enabledKey: 'city',
      type: FilterInput.CHECKBOX,
      admin: true,
      config: filters.cities as FilterConfigToggle,
      extraProps: {
        variant: Variant.INLINE_SPACE_BETWEEN,
        className: styles.fullWidth
      }
    },
    {
      display: 'State',
      key: 'states',
      enabledKey: 'state',
      type: FilterInput.CHECKBOX,
      admin: true,
      config: filters.states as FilterConfigToggle
    },
    {
      display: 'Country',
      key: 'countries',
      enabledKey: 'country',
      type: FilterInput.CHECKBOX,
      admin: true,
      config: filters.countries as FilterConfigToggle
    }
  ];

  return allFilters.filter(filter => enabledFilters.includes(filter.enabledKey));
};

const MobileFiltersModal = ({
  clearAllFilters,
  context,
  enabledFilters,
  filterConfigs,
  handleClose,
  handleFilterRadioToggle,
  handleFilterSet,
  handleFilterSlider,
  handleFilterToggle,
  resultsCount
}: Props) => {
  const mappedFilters = getMappedFilters(filterConfigs, enabledFilters);

  const buttonText =
    resultsCount === null
      ? StringConstantsMixin.strings.FILTER
      : StringConstantsMixin.strings.SEE_RESULTS.replace('{resultsCount}', `${resultsCount}`);

  const items: ReactNode[] = [];

  mappedFilters.forEach(filter => {
    if (enabledFilters.indexOf(filter.enabledKey) === -1) {
      return;
    }

    if (filter.admin && !context?.adminUser) {
      return;
    }

    items.push(
      <ExpandableFilterPanel key={filter.key} tabIndex={items.length} title={filter.display} shouldTruncate={filter.truncate} hasDividerBars>
        <FilterInputFactory
          filter={filter}
          onChange={(changes: FilterConfig) => {
            const updated = { ...filter };
            updated.config = changes;
            saveFilterChanges(updated, filter, {
              [FilterInput.CHECKBOX]: handleFilterToggle,
              [FilterInput.RADIO]: handleFilterRadioToggle,
              [FilterInput.SLIDER]: handleFilterSlider,
              [FilterInput.STAR_RATING]: handleFilterSet
            });
          }}
        />
      </ExpandableFilterPanel>
    );
  });

  return (
    <Modal handleBackgroundClick={handleClose}>
      <div className={styles.container}>
        <button className="close" onClick={handleClose} />
        <button className={styles.actionHeader} onClick={clearAllFilters}>
          {StringConstantsMixin.strings.RESET}
        </button>
        <div className={styles.title}>Filters</div>
        <div className={styles.scrollable}>{items}</div>
        <div className={styles.ctaFooter}>
          <Button variant="primary" onClick={handleClose}>
            {buttonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MobileFiltersModal;
